import { FC } from "react";
import { useTranslation } from "react-i18next";

import { TableHeader } from "@/components/Layout/Table/TableHeader/TableHeader";
import { SortableHeaderCell } from "@/components/Layout/Table/TableSortButton/TableSortButton";
import { AdditionalColumnMap } from "@/components/Portal/components/PortalDeviceListFilter/types";
import { TranslationKey } from "@/utils/translationKey";

interface Props {
  additionalColumns?: AdditionalColumnMap;
}

type Column = {
  dataTestid: string;
  /** Key in the `portal` namespace unless prefixed with `translation:`. */
  titleKey: TranslationKey;
  /** Omitted for columns the API cannot sort by. */
  sortField?: string;
  /** Only shown when this additional column is switched on. */
  additionalColumn?: keyof AdditionalColumnMap;
  className?: string;
};

const columns: Column[] = [
  {
    dataTestid: "th-status",
    className: "pl-6",
    titleKey: "common.device.attributes.status",
    sortField: "STATUSSIM",
  },
  {
    dataTestid: "th-device-name",
    titleKey: "translation:Common.label.subscriptionName",
    sortField: "SIMALIAS",
  },
  {
    dataTestid: "th-device-simType",
    titleKey: "common.device.attributes.simType",
    sortField: "SIMTYP",
  },
  {
    dataTestid: "th-device-flat",
    titleKey: "common.device.attributes.tariff",
    sortField: "TARIF",
  },
  {
    dataTestid: "th-voice-option",
    titleKey: "common.device.attributes.mobileSubscription",
    sortField: "VOICE",
  },
  {
    dataTestid: "th-runtime",
    titleKey: "common.device.attributes.openRuntime",
    sortField: "REMAININGDAYS",
  },
  {
    dataTestid: "th-followups",
    titleKey: "common.device.attributes.followupPackage",
    sortField: "SUCCESSOR",
  },
  {
    dataTestid: "th-roaming-consumption",
    titleKey: "common.device.attributes.roamingDataConsumption",
    additionalColumn: "roamingDataConsumption",
  },
  {
    dataTestid: "th-roaming-open-runtime",
    titleKey: "common.device.attributes.roamingOpenRuntime",
    additionalColumn: "roamingOpenRuntime",
  },
  {
    dataTestid: "th-roaming-status",
    titleKey: "common.device.attributes.roamingStatus",
    sortField: "STATUSROAMING",
    additionalColumn: "roamingStatus",
  },
  {
    dataTestid: "th-number",
    titleKey: "common.device.attributes.telephoneNumber",
    additionalColumn: "telephoneNumber",
  },
  {
    dataTestid: "th-ICCID",
    titleKey: "common.device.attributes.iccid",
    sortField: "ICCID",
    additionalColumn: "iccid",
  },
];

export const DeviceListHeaderRow: FC<Props> = ({ additionalColumns }) => {
  const { t } = useTranslation(["portal", "translation"]);

  return (
    <TableHeader>
      {columns
        .filter(
          ({ additionalColumn }) =>
            !additionalColumn || additionalColumns?.[additionalColumn],
        )
        .map(({ dataTestid, className, titleKey, sortField }) =>
          sortField ? (
            <SortableHeaderCell
              key={dataTestid}
              dataTestid={dataTestid}
              className={className}
              title={t(titleKey)}
              sortField={sortField}
            />
          ) : (
            <th key={dataTestid} data-testid={dataTestid}>
              {t(titleKey)}
            </th>
          ),
        )}
      <th className="center">{t("translation:Common.label.manage")}</th>
    </TableHeader>
  );
};
